

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Grid2,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { AgGridTable } from "../../Component/AgGridTable";
import BusinessIncentiveChart from "../../Component/BusinessIncentiveChart";
import SalaryReportChart from "../../Component/SalaryReportChart";
import SalesPieChart from "../../Component/SalesPieChart";
import { fetchGetMonths, fetchGetYears } from "../../services/commonService";
import { fetchIncentiveSummary } from "../../services/dashboardService";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

import { ColDef } from "ag-grid-community";

interface DropdownOption {
  tableID: number;
  tableName: string;
}

const Dashboard: React.FC = () => {
  const [month, setMonth] = useState<number | "">("");
  const [months, setMonths] = useState<DropdownOption[]>([]);
  const [year, setYear] = useState<number | "">("");
  const [years, setYears] = useState<DropdownOption[]>([]);
  const [employeeData, setEmployeeData] = useState<any[]>([]);
  const [incentiveCards, setIncentiveCards] = useState<any[]>([]);

  const getDeviceFingerprint = async (): Promise<string> => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const visitorId = result.visitorId;
    console.log(visitorId);
    return visitorId;
  };

  useEffect(() => {
    getDeviceFingerprint();
  }, []);

  useEffect(() => {
    const loadMonthYear = async () => {
      try {
        const monthData = await fetchGetMonths();
        const validMonths = Array.isArray(monthData.data) ? monthData.data : [];
        setMonths(validMonths);

        const yearData = await fetchGetYears();
        const validYears = Array.isArray(yearData.data) ? yearData.data : [];
        setYears(validYears);

        // Current month and year
        const currentMonthName = new Date()
          .toLocaleString("default", { month: "short" })
          .toLowerCase(); // e.g. "jul"
        const currentYearStr = new Date().getFullYear().toString(); // e.g. "2025"

        if (!month && validMonths.length > 0) {
          const defaultMonth =
            validMonths.find(
              (m: { tableName: string }) =>
                m.tableName.toLowerCase() === currentMonthName
            ) || validMonths[0];
          setMonth(defaultMonth.tableID);
        }

        if (!year && validYears.length > 0) {
          const defaultYear =
            validYears.find(
              (y: { tableName: string }) => y.tableName === currentYearStr
            ) || validYears[0];
          setYear(defaultYear.tableID);
        }
      } catch (error) {
        console.error("Error loading month or year:", error);
      }
    };

    loadMonthYear();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (typeof month === "number" && typeof year === "number") {
        try {
          const response = await fetchIncentiveSummary(month, year);
          const rawData = Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

          setEmployeeData(rawData);

          const defaultEmployees = ["Anbu", "Daya", "Srini"];
          const incentiveMap = new Map<string, number>();
          defaultEmployees.forEach((name) => incentiveMap.set(name, 0));
          rawData.forEach((item: { employee: string; incentive: number }) => {
            const apiName = item.employee?.trim() ?? "";
            const matchedName = defaultEmployees.find(
              (name) => name.toLowerCase() === apiName.toLowerCase()
            );
            if (matchedName) {
              incentiveMap.set(
                matchedName,
                (incentiveMap.get(matchedName) || 0) + (item.incentive ?? 0)
              );
            }
          });

          const groupedData = defaultEmployees.map((employee) => ({
            employee,
            incentive: incentiveMap.get(employee) || 0,
          }));

          setIncentiveCards(groupedData);
        } catch (error) {
          console.error("Error fetching incentive summary:", error);
        }
      }
    };

    loadData();
  }, [month, year]);

  const handleMonthChange = (e: any) => setMonth(Number(e.target.value));
  const handleYearChange = (e: any) => setYear(Number(e.target.value));

  const summarycolumns: ColDef[] = [
     { field: "employee", headerName: "BDM", sortable: true },
    { field: "customer", headerName: "Customer", sortable: true },
    {
      field: "revenue",
      headerName: "Sales",
      sortable: true,
      cellClass: "right-align",
      cellRenderer: (params: any) =>
        params.value != null ? params.value.toLocaleString("en-IN") : "0",
    },
    {
      field: "salary",
      headerName: "Payroll",
      sortable: true,
      cellClass: "right-align",
      cellRenderer: (params: any) =>
        params.value != null ? params.value.toLocaleString("en-IN") : "0",
    },
    {
      field: "otherIncentive",
      headerName: "Other",
      sortable: true,
      cellClass: "right-align",
      cellRenderer: (params: any) =>
        params.value != null ? params.value.toLocaleString("en-IN") : "0",
    },
    {
      field: "incentive",
      headerName: "Incentive",
      sortable: true,
      cellClass: "right-align",
      cellRenderer: (params: any) =>
        params.value != null ? params.value.toLocaleString("en-IN") : "0",
    },
    
  ];

  return (
    <Box
      sx={{ padding: 3, paddingTop: 3, backgroundColor: "rgb(238, 242, 246)" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">Dashboard</Typography>
        <Box
          sx={{ display: "flex", paddingBottom: 2, gap: 5, flexWrap: "wrap" }}
        >
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Month</InputLabel>
            <Select
              value={month}
              label="Month"
              onChange={handleMonthChange}
              IconComponent={() => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (months.length && typeof month === "number") {
                        const currentIndex = months.findIndex(
                          (m) => m.tableID === month
                        );
                        const prevIndex =
                          (currentIndex - 1 + months.length) % months.length;
                        setMonth(months[prevIndex].tableID);
                      }
                    }}
                  >
                    <ArrowBackIos fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (months.length && typeof month === "number") {
                        const currentIndex = months.findIndex(
                          (m) => m.tableID === month
                        );
                        const nextIndex = (currentIndex + 1) % months.length;
                        setMonth(months[nextIndex].tableID);
                      }
                    }}
                  >
                    <ArrowForwardIos fontSize="inherit" />
                  </IconButton>
                </Box>
              )}
            >
              {months.map((m) => (
                <MenuItem key={m.tableID} value={m.tableID}>
                  {m.tableName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Year</InputLabel>
            <Select
              value={year}
              label="Year"
              onChange={handleYearChange}
              IconComponent={() => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                 
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (years.length && typeof year === "number") {
                        const currentIndex = years.findIndex(
                          (y) => y.tableID === year
                        );
                        if (currentIndex < years.length - 1) {
                          setYear(years[currentIndex + 1].tableID);
                        }
                      }
                    }}
                  >
                    
                    <ArrowBackIos fontSize="inherit" />
                  </IconButton>
                   <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (years.length && typeof year === "number") {
                        const currentIndex = years.findIndex(
                          (y) => y.tableID === year
                        );
                        if (currentIndex > 0) {
                          setYear(years[currentIndex - 1].tableID);
                        }
                      }
                    }}
                  >
                   <ArrowForwardIos fontSize="inherit" />
                  </IconButton>
                </Box>
              )}
            >
              {years.map((y) => (
                <MenuItem key={y.tableID} value={y.tableID}>
                  {y.tableName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid2 container spacing={2} columns={13} mb={2}>
        <Grid2 size={{ xs: 12, sm: 12, md: 13, lg: 9, xl: 9 }}>
          <Grid2 container spacing={2} columns={12}>
            {incentiveCards.length > 0
              ? incentiveCards.map((emp, idx) => (
                  <Grid2
                    key={idx}
                    size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
                  >
                    <Card
                      sx={{
                        boxShadow: "0px 0px 50px 1px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <CardContent>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="body2" mb={1}>
                              {emp.employee}
                            </Typography>
                            <Typography variant="h5" fontWeight={500}>
                              ₹{emp.incentive.toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid2>
                ))
              : ["Anbu", "Daya", "Srini"].map((name, idx) => (
                  <Grid2
                    key={idx}
                    size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
                  >
                    <Card
                      sx={{
                        boxShadow: "0px 0px 50px 1px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <CardContent>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="body2" mb={1}>
                              {name}
                            </Typography>
                            <Typography variant="h5" fontWeight={500}>
                              ₹{(0).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid2>
                ))}

            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <Card
                sx={{
                  boxShadow: "0px 0px 50px 1px rgba(0, 0, 0, 0.1)",
                }}
              >
                <CardContent>
                  <AgGridTable
                    domLayout="autoHeight"
                    customStyles="legacy"
                    columns={summarycolumns}
                    data={employeeData}
                    pagination={false}
                    paginationPerPage={0}
                    rowSelection="single"
                    handleSelectedRows={(rows: any) => console.log(rows)}
                    noDataComponentText="No employees found."
                    gridOptions={{ suppressHorizontalScroll: false }}
                  />
                </CardContent>
              </Card>
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2
          size={{ xs: 12, sm: 12, md: 13, lg: 4, xl: 4 }}
          sx={{ display: "flex" }}
        >
          <Card
            sx={{
              flex: 1,
              boxShadow: "0px 0px 50px 1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent>
              {typeof month === "number" && typeof year === "number" && (
                <SalesPieChart month={month} year={year} />
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      <Grid2 container spacing={1}>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
          <Card
            sx={{
              boxShadow: "0px 0px 50px 1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent>
              {typeof month === "number" && typeof year === "number" && (
                <SalaryReportChart month={month} year={year} />
              )}
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
          <Card
            sx={{
              boxShadow: "0px 0px 50px 1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent>
              {typeof month === "number" && typeof year === "number" && (
                <BusinessIncentiveChart month={month} year={year} />
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default Dashboard;
